import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assert} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import {InaccessibleInfo, isInaccessibleInfo} from 'auth/RequestAccess';
import DataConnector from 'connect/DataConnector';
import {NavStoreRegistry} from 'constants/RegistryConstants';
import {Location, StorableSummary} from 'constants/SampleConstants';
import FileBrowserUtil from 'file-browser/FileBrowserUtil';
import {QueryingStorableValidationStatusIcon} from 'inventory/location-validation/StorableValidationStatusIcon';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import StorableSummaryExtrasManager from 'models/managers/StorableSummaryExtrasManager';
import {CrumbLink, SplitBreadcrumbs} from 'nav/Breadcrumbs';
import StorableStore from 'samples/StorableStore';
import * as FilterUtil from 'search/FilterUtil';
import {SavedSearch} from 'search/saved-searches/SavedSearchUtil';
import {Filter, FilterKey, Operator, SearchParams} from 'search/SearchConstants';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';

import StorableSettingsIcon from './StorableSettingsIcon';

interface Props {
  storableId: string | null;
  onNavigateToRegistry: () => void;
  onNavigateToRoot: () => void;
  onNavigateToStorable: (filters: Array<Filter>) => void;
  registry: NavStoreRegistry;
  searchParams: SearchParams;
  activeSavedSearch?: SavedSearch;
  settingsComponents?: JSX.Element;
}
interface EnhancedProps {
  ancestralStorableIds: Array<string> | null;
  storableOrInaccessibleInfoById: {[id: string]: StorableSummary | InaccessibleInfo};
  permissionsByStorableId: {[id: string]: Permissions};
}

export class StorableListingCrumbsConnector extends DataConnector<Props, EnhancedProps> {
  stores = [StorableStore];

  enhanceProps(): EnhancedProps {
    if (this.props.storableId) {
      StorableSummaryExtrasManager.requestFetch(this.props.storableId);
    }
    const extras = this.storableStore().getStorableSummaryExtras();
    if (
      this.props.storableId &&
      extras.ancestralStorableIdsByStorableId &&
      extras.ancestralStorableIdsByStorableId[this.props.storableId]
    ) {
      const ancestralStorableIds = extras.ancestralStorableIdsByStorableId[this.props.storableId];
      const storableOrInaccessibleInfoById = this.getOrFetchMultipleMaybeInaccessibleRequired(
        ManagedModelType.STORABLE_SUMMARY,
        ancestralStorableIds
      );
      const permissionsByStorableId = this.getOrFetchMultipleRequired(
        ManagedModelType.PERMISSIONS_BY_ITEM_ID,
        [this.props.storableId]
      );
      return {
        ancestralStorableIds,
        storableOrInaccessibleInfoById,
        permissionsByStorableId,
      };
    }
    return {ancestralStorableIds: null, storableOrInaccessibleInfoById: {}, permissionsByStorableId: {}};
  }
}

interface StorableCrumbLinkProps {
  storable: StorableSummary;
  onNavigateToStorable: (filters: Array<Filter>) => void;
  searchParams: SearchParams;
}

function getStorableCrumbLink(props: StorableCrumbLinkProps): JSX.Element {
  function handleClick(): void {
    props.onNavigateToStorable(
      FilterUtil.replaceByKey(props.searchParams.filters, {
        key: FilterKey.PARENT_STORABLE,
        operator: Operator.IS,
        value: props.storable.id,
      })
    );
  }
  return <CrumbLink key={props.storable.id} text={props.storable.name} onClick={handleClick} />;
}

export function StorableListingCrumbs(props: Props & EnhancedProps): JSX.Element {
  if (props.storableId != null && !props.ancestralStorableIds) {
    return <HorizontalThrobber />;
  }

  const crumbLinks = [<CrumbLink key="root" text="Inventory" onClick={props.onNavigateToRoot} />];
  if (props.registry) {
    crumbLinks.push(
      <CrumbLink
        key="registry"
        text={FileBrowserUtil.getRegistryName(props.registry)}
        onClick={props.onNavigateToRegistry}
      />
    );
  }
  if (props.storableId) {
    assert(Boolean(props.ancestralStorableIds), 'Expected ancestralStorableIds to be fetched');
    for (const storableId of props.ancestralStorableIds!.slice().reverse()) {
      const storableOrInaccessibleInfo = props.storableOrInaccessibleInfoById[storableId];
      if (!isInaccessibleInfo(storableOrInaccessibleInfo)) {
        const storable = storableOrInaccessibleInfo as StorableSummary;
        crumbLinks.push(
          getStorableCrumbLink({
            storable,
            onNavigateToStorable: props.onNavigateToStorable,
            searchParams: props.searchParams,
          })
        );
      }
    }
  }
  if (props.activeSavedSearch) {
    crumbLinks.push(<CrumbLink key="savedSearch" text={props.activeSavedSearch.name} />);
  }
  const location =
    props.storableId && !isInaccessibleInfo(props.storableOrInaccessibleInfoById[props.storableId])
      ? ({
          ...props.storableOrInaccessibleInfoById[props.storableId],
          permissions: props.permissionsByStorableId[props.storableId],
        } as Location)
      : null;
  return (
    <SplitBreadcrumbs
      crumbs={crumbLinks}
      settingsIcon={
        <>
          <StorableSettingsIcon
            registry={props.registry}
            location={location}
            iconClassName={css(styles.settingsIcon)}
          />
          {props.settingsComponents}
        </>
      }
      trailingCrumbIcon={
        props.storableId != null ? (
          <QueryingStorableValidationStatusIcon
            className={css(styles.validationStatusIcon)}
            storableId={props.storableId}
          />
        ) : undefined
      }
    />
  );
}

const styles = StyleSheet.create({
  settingsIcon: {verticalAlign: 0},
  validationStatusIcon: {
    height: '14px',
    marginLeft: '6px',
    marginRight: '2px',
  },
});

export default StorableListingCrumbsConnector.connect(StorableListingCrumbs);
